import Utilities from "./utilities";

let markerZindex = 1;
let customMarkers = [];

const Markers = {
	init: async ( tm ) => {
		return new Promise( resolve => {
			if ( tmVars.markersPath === '' ) {
				resolve();
				return;
			}

			const url = tmVars.gtfsDir + tmVars.markersPath;
			Utilities.cachedFetch( url ).then( ( response ) => {
				return response.ok ? response.json() : Promise.reject( response );
			} ).then( async ( data ) => {
				customMarkers = await Markers.addMarkersToMap( tm, data )
				tm.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push( Markers.createControl( data ) )

				// hacky but seemingly unavoidable way to distinguish clicks on opened or unopened markers
				// marker opens when you click it, unless clicking on the provider of an already-opened marker
				tm.$map.addEventListener( 'click', event => Markers.recordClickOnMarkerProvider( event ), {capture: true} );
				resolve();
			} ).catch( ( err ) => {
				console.error( 'Error:', err );
				resolve();
			} );
		} );
	},

	addMarkersToMap: async ( tm, data ) => {
		const {AdvancedMarkerElement} = await google.maps.importLibrary( 'marker' );

		let markers = [],
			entityDecoder = document.createElement( 'textarea' );
		for ( let provider of data ) {
			if ( !provider.geojson ) continue;
			entityDecoder.innerHTML = provider.name;

			for ( const feature of provider.geojson.features ) {
				const marker = new AdvancedMarkerElement( {
					map: tm.map,
					content: Markers.renderMarkerContent( provider, feature.properties ),
					position: {lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0]},
					title: entityDecoder.value + ' ' + feature.properties.title
				} );
				if ( provider.svgPin ) {
					marker.zIndex = 0;
				} else {
					marker.zIndex = 1;
					marker.addListener( 'click', () => Markers.toggleMarkerExpanded( marker ) );
				}

				markers.push( marker );
			}
		}

		return markers;
	},

	renderMarkerContent: ( provider, markerProperties ) => {
		const content = document.createElement( 'div' );
		content.classList.add( 'tm__marker' );
		content.classList.add( 'is-provider-' + provider.id );
		content.setAttribute( 'aria-hidden', 'true' );
		content.setAttribute( 'inert', '' );
		if ( !provider.svgPin ) content.classList.add( 'no-custom-pin' );
		content.innerHTML = `
			<div class='tm__marker-provider'>
				${provider.svgPin ? provider.svgPin : provider.name + provider.svgIcon}
			</div>`;
		if ( !provider.svgPin && (markerProperties.title || markerProperties.desc || markerProperties.links) ) {
			content.innerHTML += `
				<div class="tm__marker-content">
					${markerProperties.title ? '<div class="tm__marker-title">' + markerProperties.title + '</div>' : ''}
					${markerProperties.desc ? '<div class="tm__marker-desc">' + markerProperties.desc + '</div>' : ''}
					${markerProperties.links ? '<div class="tm__marker-links">' + markerProperties.links + '</div>' : ''}
				</div>`;
		}
		return content;
	},

	toggleMarkerExpanded: ( marker ) => {
		let openMarker = (!marker.content.classList.contains( 'is-expanded' ) || marker.content.dataset.clickedProvider !== 'true')
			? marker
			: null;
		Markers.expandMarker( openMarker );
		marker.content.dataset.clickedProvider = '';
	},

	recordClickOnMarkerProvider: ( event ) => {
		if ( event.target.classList.contains( 'tm__marker-provider' ) ) {
			event.target.parentNode.dataset.clickedProvider = 'true';
		} else {
			Markers.expandMarker();
		}
	},

	expandMarker: ( openMarker = null ) => {
		customMarkers.forEach( closeMarker => {
			closeMarker.content.classList.remove( 'is-expanded' );
			closeMarker.content.setAttribute( 'aria-hidden', 'true' );
			closeMarker.content.setAttribute( 'inert', '' );
		} )
		if ( openMarker ) {
			openMarker.content.classList.add( 'is-expanded' );
			openMarker.content.setAttribute( 'aria-hidden', 'false' );
			openMarker.content.removeAttribute( 'inert' );
			openMarker.zIndex = markerZindex ++;
		}
	},

	createControl: ( data ) => {
		if ( !data.length ) return;

		let control = document.createElement( 'div' );
		control.id = 'tm__map-control';
		control.innerHTML = Markers.controlRenderHtml( data );

		// on desktop, expand control on load, unless it has been manually collapsed
		if ( Utilities.isDesktop() && !localStorage.getItem( 'map-control-collapsed' ) ) control.classList.add( 'toggled' );

		// toggle control display when clicked
		control.querySelector( 'button' ).addEventListener( 'click', () => {
			if ( control.classList.contains( 'toggled' ) ) {
				control.classList.remove( 'toggled' );
				localStorage.setItem( 'map-control-collapsed', '1' );
			} else {
				control.classList.add( 'toggled' );
				localStorage.removeItem( 'map-control-collapsed' );
			}
		} );

		[...control.querySelectorAll( 'input' )].forEach( ( $input ) => {
			// toggle marker display when checkboxes are changed
			$input.addEventListener( 'change', ( e ) => {
				Markers.toggleMarkersDisplay( e.target.checked, e.target.id.replace( 'provider-input', 'provider' ) );
				Markers.controlSynchronizeChildrenWithParent( control, e.target.dataset.children, e.target.checked );
				Markers.controlSynchronizeParentsWithChildren( control, e.target.dataset.parent );
				Markers.controlStoreCheckedInputs( control );
			} )

			// initialize checked checkboxes (ignore unchecked parents; otherwise they would uncheck their potentially-checked children)
			if ( !$input.dataset.children || $input.checked ) $input.dispatchEvent( new Event( 'change' ) )
		} );

		return control;
	},

	controlRenderHtml: ( data ) => {
		const cached = JSON.parse( localStorage.getItem( 'checked-map-options' ) );
		let html = '<button type="button">Map Options</button><div class="tm__map-options">',
			providerHash = '';

		if ( window.location.hash && window.location.hash.substring( 0, 10 ) === '#provider-' ) {
			providerHash = parseInt( window.location.hash.substring( 10 ), 10 );
		}

		for ( const provider of data ) {
			let checked, svgIcon = '';

			if ( providerHash ) {
				// override the checked value, if showing only a specific provider via an anchor/fragment in the URL
				checked = providerHash === provider.id;
			} else {
				// if cache exists, load cached checked values; otherwise use default checked value for each provider
				checked = cached ? cached.indexOf( provider.id ) !== - 1 : provider.checked;
			}

			if ( provider.svgIcon ) {
				svgIcon = ' ' + provider.svgIcon.replace( '<svg ', '<svg role="presentation" ' );
			}

			html += `<input type="checkbox" name="providers" id="provider-input-${provider.id}" 
				${provider.children ? 'data-children="' + provider.children + '"' : ''}
				${provider.parent ? 'data-parent="' + provider.parent + '"' : ''}
				${checked ? 'checked' : ''}>
				<label for="provider-input-${provider.id}" tabindex="0">${provider.name}${svgIcon}</label>`;
		}
		html += '</div>'
		return html;
	},

	controlStoreCheckedInputs: ( control ) => {
		let checked = control.querySelectorAll( "input:checked" ),
			checkedIds = Array.from( checked, node => parseInt( node.id.slice( 15 ), 10 ) );
		localStorage.setItem( 'checked-map-options', JSON.stringify( checkedIds ) );
	},

	controlSynchronizeChildrenWithParent: ( control, children, isTogglingOn ) => {
		if ( !children ) return;
		children = children.split( ',' );
		for ( let i in children ) {
			let $el = control.querySelector( '#provider-input-' + children[i] );
			$el.checked = isTogglingOn;
			Markers.toggleMarkersDisplay( isTogglingOn, 'provider-' + children[i] );
		}
	},

	controlSynchronizeParentsWithChildren: ( control, parent ) => {
		if ( !parent ) return;
		let $input = control.querySelector( '#provider-input-' + parent ),
			children = $input.dataset.children.split( ',' );
		let toggledCount = 0;
		for ( let i in children ) {
			let $el = control.querySelector( '#provider-input-' + children[i] );
			toggledCount += $el.checked ? 1 : - 1;
		}
		let isTogglingOn = (toggledCount === children.length);
		$input.checked = isTogglingOn;
		Markers.toggleMarkersDisplay( isTogglingOn, 'provider-' + $input.id );
	},

	// shows or hides markers; pass a provider ID to affect only that agency, otherwise affects all
	toggleMarkersDisplay: ( isTogglingOn, providerId = null ) => {
		customMarkers.forEach( marker => {
			if ( providerId && !marker.content.classList.contains( 'is-' + providerId ) ) return;
			if ( isTogglingOn ) {
				marker.content.classList.add( 'is-visible' );
			} else {
				marker.content.classList.remove( 'is-visible' );
				marker.content.classList.remove( 'is-expanded' );
			}
		} )
	},
}

export default Markers;
